import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
  Firestore,
} from 'firebase/firestore';
import type { Functions } from 'firebase/functions';
import { Unit } from '../types';

export interface MembershipRequest {
  id: string;
  unitId: string;
  unitName: string;
  userId: string;
  userEmail: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: Date;
  requestType: 'duplicate_attempt' | 'direct_request';
  attemptedUnitName?: string;
  attemptedUnitNumber?: string;
}

export function useUnitMembershipRequests(
  db: Firestore,
  functions: Functions | null | undefined,
  unitId: string | undefined,
  unit: Unit | null,
  currentUser: { uid: string } | null,
  activeTab: string,
  canEdit: boolean,
  showSuccess: (message: string) => void,
  showError: (message: string) => void
) {
  const [membershipRequests, setMembershipRequests] = useState<MembershipRequest[]>([]);
  const [processingRequestId, setProcessingRequestId] = useState<string | null>(null);

  useEffect(() => {
    const fetchRequests = async () => {
      if (!unitId || activeTab !== 'invitations' || !canEdit) return;

      try {
        const requestsQuery = query(
          collection(db, 'membershipRequests'),
          where('unitId', '==', unitId),
          where('status', '==', 'pending')
        );
        const snapshot = await getDocs(requestsQuery);
        const requests: MembershipRequest[] = snapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            unitId: data.unitId,
            unitName: data.unitName,
            userId: data.userId,
            userEmail: data.userEmail,
            status: data.status,
            createdAt: data.createdAt?.toDate?.() || new Date(data.createdAt),
            requestType: data.requestType || 'direct_request',
            attemptedUnitName: data.attemptedUnitName,
            attemptedUnitNumber: data.attemptedUnitNumber,
          };
        });
        setMembershipRequests(requests);
      } catch (error) {
        console.error('Error fetching membership requests:', error);
      }
    };

    fetchRequests();
  }, [unitId, activeTab, canEdit, db]);

  const handleApproveRequest = async (request: MembershipRequest) => {
    if (!unit || !currentUser) return false;

    if (!functions) {
      showError('Membership approval service is not available. Please refresh and try again.');
      return false;
    }

    setProcessingRequestId(request.id);
    try {
      const { httpsCallable } = await import('firebase/functions');
      const approveMembershipRequest = httpsCallable<
        { requestId: string },
        { success: boolean; alreadyMember?: boolean }
      >(functions, 'approveMembershipRequest');

      const result = await approveMembershipRequest({ requestId: request.id });

      setMembershipRequests((prev) => prev.filter((r) => r.id !== request.id));
      if (result.data.alreadyMember) {
        showSuccess('User is already a member. Request marked as approved.');
      } else {
        showSuccess(`${request.userEmail} has been added as a member`);
      }
      return true;
    } catch (error: any) {
      console.error('Error approving membership request:', error);
      const code = error?.code;
      if (code === 'functions/permission-denied' || code === 'permission-denied') {
        showError('You do not have permission to approve membership requests');
      } else if (code === 'functions/not-found' || code === 'not-found') {
        showError(error?.message || 'Membership request or user not found');
      } else {
        showError('Failed to approve membership request');
      }
      return false;
    } finally {
      setProcessingRequestId(null);
    }
  };

  const handleRejectRequest = async (request: MembershipRequest) => {
    if (!currentUser) return false;

    setProcessingRequestId(request.id);
    try {
      await updateDoc(doc(db, 'membershipRequests', request.id), {
        status: 'rejected',
        processedAt: new Date(),
        processedBy: currentUser.uid,
      });

      setMembershipRequests((prev) => prev.filter((r) => r.id !== request.id));
      showSuccess('Membership request rejected');
      return true;
    } catch (error) {
      console.error('Error rejecting membership request:', error);
      showError('Failed to reject membership request');
      return false;
    } finally {
      setProcessingRequestId(null);
    }
  };

  return {
    membershipRequests,
    processingRequestId,
    handleApproveRequest,
    handleRejectRequest,
  };
}
